import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { API_BASE } from "./api";

const GENERIC_SUCCESS_MESSAGE =
  "If an account exists for that email, password reset instructions have been sent.";

const UNAVAILABLE_MESSAGE =
  "Password reset email is not configured in this environment. Contact an operator or see local setup docs.";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordResetAvailable, setPasswordResetAvailable] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/auth/password-reset-status`)
      .then((res) => {
        if (!cancelled) {
          setPasswordResetAvailable(Boolean(res.data?.available));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPasswordResetAvailable(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await axios.post(`${API_BASE}/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Could not process your request. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (passwordResetAvailable === null) {
    return (
      <div className="auth-container">
        <div className="auth-form-container">
          <h2 className="auth-title">Forgot password</h2>
          <p>Checking password reset availability…</p>
        </div>
      </div>
    );
  }

  if (!passwordResetAvailable) {
    return (
      <div className="auth-container">
        <div className="auth-form-container">
          <h2 className="auth-title">Forgot password</h2>
          <div className="error-message" role="status">
            <AlertCircle size={16} />
            <span>{UNAVAILABLE_MESSAGE}</span>
          </div>
          <a href="/login" className="toggle-auth-mode">
            Back to login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-form-container">
        <h2 className="auth-title">Forgot password</h2>

        {submitted ? (
          <div className="success-message" role="status">
            <CheckCircle2 size={16} />
            <span>{GENERIC_SUCCESS_MESSAGE}</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                required
              />
            </div>

            {error ? (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            ) : null}

            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? "Sending..." : "Send reset instructions"}
            </button>
          </form>
        )}

        <a href="/login" className="toggle-auth-mode">
          Back to login
        </a>
      </div>
    </div>
  );
}
